import {
  DEFAULT_TIMEZONE,
  DATETIME_DISPLAY_FORMAT,
  TIME_DISPLAY_FORMAT,
  type Languages,
  LOCALES,
} from '@avanta/shared/constants/common';
import { getLanguage } from '@avanta/shared/utils/date';
import { format, isAfter, startOfDay, addDays } from 'date-fns';
import { fromZonedTime, toZonedTime, format as formatTz } from 'date-fns-tz';
import { useUserStore } from 'stores/user';

export { isMoreThanSecondsBefore, formatDateISO } from '@avanta/shared/utils/date';

const getUserTz = (): string => useUserStore.getState().user?.timeZone ?? DEFAULT_TIMEZONE;

export const getMonthName = (month: number, template = 'MMM'): string =>
  format(new Date(2000, month - 1), template, {
    locale: LOCALES[getLanguage() as Languages],
  });
/**
 * Formats datetime from unified format for display, automatically converted
 * to the user's timezone (read from the store). Dates are stored as Europe/Madrid.
 * @example formatUnifiedForDisplay('2025-10-14T10:30:00') // "14/10/2025 10h30"
 * @example formatUnifiedForDisplay('2025-10-14T10:30:00', 'dd/MM/yyyy') // "14/10/2025"
 */
export function formatUnifiedForDisplay(
  unifiedDateTime: string,
  displayFormat = DATETIME_DISPLAY_FORMAT,
  timezone?: string
): string {
  return formatForUserTz(unifiedDateTime, timezone ?? getUserTz(), displayFormat);
}

/**
 * Formats the time part of a unified datetime with the app time mask,
 * automatically converted to the user's timezone.
 * @example formatUnifiedTime('2025-10-14T10:30:00') // "04h30" (if user is in America/New_York)
 */
export function formatUnifiedTime(unifiedDateTime: string, timezone?: string): string {
  return formatUnifiedForDisplay(unifiedDateTime, TIME_DISPLAY_FORMAT, timezone);
}

/**
 * Formats the date part of a unified datetime using the locale-specific simple format
 * (dd/MM/yyyy for es/ca, MM/dd/yyyy for en), automatically converted to the user's timezone.
 * @example formatUnifiedDateSimple('2025-10-14T10:30:00') // "14/10/2025"
 */
export function formatUnifiedDateSimple(unifiedDateTime: string): string {
  const masks: Record<Languages, string> = {
    es: 'dd/MM/yyyy',
    ca: 'dd/MM/yyyy',
    en: 'MM/dd/yyyy',
  };
  const mask = masks[getLanguage() as Languages] || masks.en;
  return formatUnifiedForDisplay(unifiedDateTime, mask);
}

/**
 * Gets current date in ISO format (yyyy-MM-dd) for API calls
 * @example getCurrentDateISO() // "2025-10-14"
 */
export function getCurrentDateISO(): string {
  return format(new Date(), 'yyyy-MM-dd');
}

/**
 * Gets date N days from now in ISO format (yyyy-MM-dd)
 * @example getDateAfterDays(7) // "2025-10-21" (7 days from now)
 */
export function getDateAfterDays(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return format(date, 'yyyy-MM-dd');
}

/**
 * Checks if a datetime string (interpreted in the given IANA timezone) is in the past.
 * @param dateTimeStr - Datetime string in unified format (e.g. '2025-10-14T10:30:00')
 * @param timezone - IANA timezone string (e.g. 'Europe/Madrid')
 * @returns true if the datetime has already passed
 */
export function isDateTimePast(dateTimeStr: string, timezone: string): boolean {
  const utcDate = fromZonedTime(dateTimeStr, timezone);
  return isAfter(new Date(), utcDate);
}

function formatForUserTz(dateTimeStr: string, userTz: string, displayFormat: string): string {
  const utcDate = fromZonedTime(dateTimeStr, DEFAULT_TIMEZONE);
  const userDate = toZonedTime(utcDate, userTz);
  return formatTz(userDate, displayFormat, {
    timeZone: userTz,
    locale: LOCALES[getLanguage() as Languages],
  });
}

/**
 * Returns true if `date` falls within [startTime, endTime) in the given timezone.
 * Times must be in 'HH:mm' format. Does not support intervals crossing midnight.
 * @example isWithinTimeInterval(new Date(), '09:00', '18:00', 'Europe/Madrid')
 */
function isWithinTimeInterval(
  date: Date,
  startTime: string,
  endTime: string,
  timezone: string
): boolean {
  const dateInTz = toZonedTime(date, timezone);
  const currentTime = formatTz(dateInTz, 'HH:mm', { timeZone: timezone });
  return currentTime >= startTime && currentTime < endTime;
}

/**
 * Returns true if the current moment falls within [startTime, endTime) in the given timezone.
 * Times must be in 'HH:mm' format. Does not support intervals crossing midnight.
 * @example isNowWithinTimeInterval('09:00', '18:00', 'Europe/Madrid')
 */
export function isNowWithinTimeInterval(
  startTime: string,
  endTime: string,
  timezone: string
): boolean {
  return isWithinTimeInterval(new Date(), startTime, endTime, timezone);
}

/**
 * Returns true if `date`'s ISO weekday (1 = Monday ... 7 = Sunday) in the given
 * timezone is included in unavailableDays.
 * @example isOnUnavailableDay(new Date(), [6, 7], 'Europe/Madrid') // true on Saturday or Sunday
 */
function isOnUnavailableDay(date: Date, unavailableDays: number[], timezone: string): boolean {
  const dateInTz = toZonedTime(date, timezone);
  const isoWeekday = Number(formatTz(dateInTz, 'i', { timeZone: timezone }));
  return unavailableDays.includes(isoWeekday);
}

/**
 * Returns true if the current ISO weekday (1 = Monday ... 7 = Sunday) in the given
 * timezone is included in unavailableDays.
 * @example isNowOnUnavailableDay([6, 7], 'Europe/Madrid') // true on Saturday or Sunday
 */
export function isNowOnUnavailableDay(unavailableDays: number[], timezone: string): boolean {
  return isOnUnavailableDay(new Date(), unavailableDays, timezone);
}

/**
 * Returns true if the availability window (defined by startTime/endTime on working days)
 * overlaps with the open interval (from, to). Iterates calendar day by day in the given
 * timezone (up to 8 days). If startTime/endTime are omitted the whole working day counts
 * as available, matching the fallback behaviour of isEmotionalChatAvailable.
 *
 * Useful for detecting whether a period of availability has elapsed between two instants,
 * e.g. "did an available window occur since the last unavailable auto-message was sent?"
 */
export function hasAvailableWindowBetween(
  from: Date,
  to: Date,
  options: {
    startTime?: string;
    endTime?: string;
    unavailableDays: number[];
    timezone: string;
  }
): boolean {
  const { startTime, endTime, unavailableDays, timezone } = options;
  if (from >= to) return false;

  // Anchor to the start of `from`'s calendar day in the target timezone
  const fromInTz = toZonedTime(from, timezone);
  const dayStart = startOfDay(fromInTz);

  for (let dayOffset = 0; dayOffset <= 7; dayOffset++) {
    const dayInTz = addDays(dayStart, dayOffset);
    const dayUtcStart = fromZonedTime(dayInTz, timezone);

    if (dayUtcStart >= to) break;

    if (isOnUnavailableDay(dayUtcStart, unavailableDays, timezone)) continue;

    const dateStr = formatTz(dayInTz, 'yyyy-MM-dd', { timeZone: timezone });

    let windowStart: Date;
    let windowEnd: Date;

    if (startTime && endTime) {
      windowStart = fromZonedTime(`${dateStr}T${startTime}:00`, timezone);
      windowEnd = fromZonedTime(`${dateStr}T${endTime}:00`, timezone);
    } else {
      // No time window configured: the whole working day is available
      windowStart = dayUtcStart;
      windowEnd = fromZonedTime(addDays(dayInTz, 1), timezone);
    }

    if (windowStart < to && windowEnd > from) {
      return true;
    }
  }

  return false;
}
